import {
  APIComponentAttributes,
  ArdoqId,
  ResourceType,
  WebSocketFieldUnappliedPayload,
  WebSocketGlobalFieldDeletedPayload,
  WebSocketGlobalFieldOptionsRemovedPayload,
} from '@ardoq/api-types';
import {
  StreamCollection,
  getStreamCollectionInitialState,
  toStreamCollection,
  upsertComponents,
} from 'streams/utils/streamUtils';
import {
  persistentReducedStream,
  reducedStream,
  reducer,
  spreadFilter,
  streamReducer,
  toPersistentStream,
} from '@ardoq/rxbeach';
import {
  bufferTime,
  filter,
  map,
  pairwise,
  share,
  withLatestFrom,
} from 'rxjs/operators';
import * as componentActions from './ComponentActions';
import { componentOperations, toIds } from '@ardoq/core';
import { DEFAULT_DEBOUNCE_TIME } from 'modelInterface/consts';
import { uniqBy } from 'lodash';
import { filterResourceType, websocket$ } from 'sync/websocket$';
import { activeBranchId$ } from 'streams/activeScenario/activeBranchId$';
import { shouldProcessEventForBranch } from 'sync/ardoqEventOperations';
import { websocketReducer } from 'streams/crud/reducers';
import {
  ArdoqEvent,
  isWebSocketFieldUnapplied,
  isWebSocketGlobalFieldDeleted,
  isWebSocketGlobalFieldOptionsRemoved,
} from 'sync/types';
import { fieldOps } from 'models/utils/fieldOps';

export type ComponentsState = StreamCollection<APIComponentAttributes> & {
  childrenByParent: Map<ArdoqId, APIComponentAttributes[]>;
};

export const toComponentsState = (
  components: APIComponentAttributes[]
): ComponentsState => ({
  ...toStreamCollection(components),
  childrenByParent: componentOperations.buildChildrenByParent(components),
});

export const emptyComponentsState: ComponentsState = {
  ...getStreamCollectionInitialState<APIComponentAttributes>(),
  childrenByParent: new Map(),
};

export type ScenarioRelatedComponentIds = Set<ArdoqId>;

export const emptyScenarioRelatedComponentIds: ScenarioRelatedComponentIds =
  new Set();

const clearScenarioRelatedComponentIds = (
  _state: ScenarioRelatedComponentIds
): ScenarioRelatedComponentIds => new Set();

const setScenarioRelatedComponentIdsReducer = (
  _state: ScenarioRelatedComponentIds,
  ids: ArdoqId[]
): ScenarioRelatedComponentIds => new Set(ids);

export const scenarioRelatedComponentIds$ =
  persistentReducedStream<ScenarioRelatedComponentIds>(
    'scenarioRelatedComponentIds$',
    emptyScenarioRelatedComponentIds,
    [
      reducer(
        componentActions.resetComponentsAction,
        clearScenarioRelatedComponentIds
      ),
      reducer(
        componentActions.resetComponentsWithData,
        clearScenarioRelatedComponentIds
      ),
      reducer(
        componentActions.setScenarioRelatedComponentIds,
        setScenarioRelatedComponentIdsReducer
      ),
    ]
  );

export const addComponents = (
  state: APIComponentAttributes[],
  components: APIComponentAttributes[]
): APIComponentAttributes[] => [...state, ...components];

export const removeComponents = (
  state: APIComponentAttributes[],
  ids: ArdoqId[]
): APIComponentAttributes[] => {
  const idSet = new Set(ids);
  return state.filter(comp => !idSet.has(comp._id));
};

export const updateComponent = (
  state: APIComponentAttributes[],
  updated: APIComponentAttributes
): APIComponentAttributes[] =>
  state.map(comp => (comp._id === updated._id ? updated : comp));

export const mergeComponents = (
  state: APIComponentAttributes[],
  components: APIComponentAttributes[]
): APIComponentAttributes[] => {
  const updateMap = new Map(components.map(c => [c._id, c]));
  const existingIds = new Set<ArdoqId>();
  const updated = state.map(comp => {
    existingIds.add(comp._id);
    return updateMap.get(comp._id) ?? comp;
  });
  const newComps = components.filter(c => !existingIds.has(c._id));
  return [...updated, ...newComps];
};

const unsetAttributeOnComponent = (
  comp: APIComponentAttributes,
  attribute: string
): APIComponentAttributes => ({
  ...comp,
  [attribute]: undefined,
  _version: comp._version + 1,
});

const addComponentsActionReducer = (
  state: APIComponentAttributes[],
  { components }: componentActions.AddComponentsPayload
): APIComponentAttributes[] => upsertComponents(state, components);

const resetComponentsReducer = (
  _state: APIComponentAttributes[]
): APIComponentAttributes[] => [];

const resetComponentsWithDataReducer = (
  _state: APIComponentAttributes[],
  { components }: componentActions.ResetComponentsWithDataPayload
): APIComponentAttributes[] => uniqBy(components, '_id');

const removeComponentsActionReducer = (
  state: APIComponentAttributes[],
  { ids }: componentActions.RemoveComponentsPayload
): APIComponentAttributes[] => removeComponents(state, ids);

const updateComponentActionReducer = (
  state: APIComponentAttributes[],
  updated: APIComponentAttributes
): APIComponentAttributes[] => updateComponent(state, updated);

const mergeComponentsActionReducer = (
  state: APIComponentAttributes[],
  { components }: componentActions.MergeComponentsPayload
): APIComponentAttributes[] => mergeComponents(state, components);

const removeGlobalFieldOptionsReducer = (
  state: APIComponentAttributes[],
  {
    data: { name, options },
  }: ArdoqEvent<WebSocketGlobalFieldOptionsRemovedPayload>
): APIComponentAttributes[] => {
  const removed = new Set(options);
  return state.map(comp => {
    const value = comp[name];
    if (value === undefined) return comp;
    const kept = fieldOps.withoutOptions(value, removed);
    if (kept === value) return comp;
    return { ...comp, [name]: kept, _version: comp._version + 1 };
  });
};

const unsetUnappliedFieldReducer = (
  state: APIComponentAttributes[],
  { data }: ArdoqEvent<WebSocketFieldUnappliedPayload>
): APIComponentAttributes[] => {
  const types = new Set(data.componentType);
  return state.map(comp => {
    if (comp[data.name] === undefined) return comp;
    if (comp.model !== data.model) return comp;
    if (!types.has(comp.typeId)) return comp;
    return unsetAttributeOnComponent(comp, data.name);
  });
};

const unsetDeletedGlobalFieldReducer = (
  state: APIComponentAttributes[],
  { data }: ArdoqEvent<WebSocketGlobalFieldDeletedPayload>
): APIComponentAttributes[] =>
  state.map(comp =>
    comp[data.name] === undefined
      ? comp
      : unsetAttributeOnComponent(comp, data.name)
  );

const deleteComponentReducer = (
  state: APIComponentAttributes[],
  id: ArdoqId
): APIComponentAttributes[] => removeComponents(state, [id]);

const componentsOnly$ = reducedStream<APIComponentAttributes[]>(
  'componentsOnly$',
  [],
  [
    reducer(componentActions.addComponentsAction, addComponentsActionReducer),
    reducer(componentActions.resetComponentsAction, resetComponentsReducer),
    reducer(
      componentActions.resetComponentsWithData,
      resetComponentsWithDataReducer
    ),
    reducer(
      componentActions.removeComponentsAction,
      removeComponentsActionReducer
    ),
    reducer(
      componentActions.updateComponentAction,
      updateComponentActionReducer
    ),
    reducer(
      componentActions.mergeComponentsAction,
      mergeComponentsActionReducer
    ),
    streamReducer(
      websocket$.pipe(
        filterResourceType<APIComponentAttributes>(ResourceType.COMPONENT),
        withLatestFrom(activeBranchId$),
        spreadFilter(shouldProcessEventForBranch)
      ),
      websocketReducer({ delete: deleteComponentReducer })
    ),
    streamReducer(
      websocket$.pipe(filter(isWebSocketGlobalFieldDeleted)),
      unsetDeletedGlobalFieldReducer
    ),
    streamReducer(
      websocket$.pipe(
        filter(isWebSocketGlobalFieldOptionsRemoved),
        withLatestFrom(activeBranchId$),
        spreadFilter(shouldProcessEventForBranch)
      ),
      removeGlobalFieldOptionsReducer
    ),
    streamReducer(
      websocket$.pipe(
        filter(isWebSocketFieldUnapplied),
        withLatestFrom(activeBranchId$),
        spreadFilter(shouldProcessEventForBranch)
      ),
      unsetUnappliedFieldReducer
    ),
  ]
);

export const components$ = toPersistentStream(
  'components$',
  componentsOnly$.pipe(map(toComponentsState)),
  emptyComponentsState
);

/**
 * Emits the IDs of components newly added to the collection.
 *
 * @deprecated Do not introduce new dependencies on this stream. Diff
 * calculations on components should be moved into an appropriate reducer for
 * {@link components$}, using `components$` as the single source of truth,
 * rather than bespoke pairwise-based diff streams.
 */
export const componentsAdded$ = components$.pipe(
  pairwise(),
  map(([prev, curr]) => toIds(curr.models.filter(c => !prev.byId[c._id]))),
  filter(ids => ids.length > 0)
);

/**
 * Emits when the set membership of component IDs changes (any add or remove).
 *
 * @deprecated Do not introduce new dependencies on this stream. Diff
 * calculations on components should be moved into an appropriate reducer for
 * {@link components$}, using `components$` as the single source of truth,
 * rather than bespoke pairwise-based diff streams.
 */
export const componentSetChanged$ = components$.pipe(
  map(({ sortIds }) => sortIds),
  pairwise(),
  filter(([prev, curr]) => {
    if (prev.length !== curr.length) return true;
    const prevSet = new Set(prev);
    return curr.some(id => !prevSet.has(id));
  })
);

/**
 * Emits the IDs of components whose `parent` property changed between
 * consecutive emissions.
 *
 * @deprecated Do not introduce new dependencies on this stream. Diff
 * calculations on components should be moved into an appropriate reducer for
 * {@link components$}, using `components$` as the single source of truth,
 * rather than bespoke pairwise-based diff streams.
 */
export const componentParentChange$ = components$.pipe(
  pairwise(),
  map(([prev, curr]) =>
    toIds(
      curr.models.filter(
        c => prev.byId[c._id] && prev.byId[c._id].parent !== c.parent
      )
    )
  ),
  filter(ids => ids.length > 0),
  share()
);

/**
 * Emits the components that changed between consecutive emissions (any
 * attribute). Detection is by reference equality on the per-component entry
 * in `byId`, which is exact because every reducer in this file produces a
 * new object reference whenever any attribute changes. If a future reducer
 * mutates in place, this stream silently misses changes.
 *
 * @deprecated Do not introduce new dependencies on this stream. Diff
 * calculations on components should be moved into an appropriate reducer for
 * {@link components$}, using `components$` as the single source of truth,
 * rather than bespoke pairwise-based diff streams.
 */
export const componentsChanged$ = components$.pipe(
  pairwise(),
  map(([prev, curr]) =>
    curr.models.filter(c => prev.byId[c._id] && prev.byId[c._id] !== c)
  ),
  filter(arr => arr.length > 0),
  share()
);

export type ComponentsDiff = {
  added: string[];
  removed: string[];
  parentChanged: string[];
};

/**
 * Emits a batched diff of added, removed, and parent-changed component IDs.
 * Batches changes within {@link DEFAULT_DEBOUNCE_TIME} ms into a single
 * emission.
 *
 * @deprecated Do not introduce new dependencies on this stream. Diff
 * calculations on components should be moved into an appropriate reducer for
 * {@link components$}, using `components$` as the single source of truth,
 * rather than bespoke pairwise-based diff streams.
 */
export const componentsDiff$ = components$.pipe(
  pairwise(),
  map(([prev, curr]) => ({
    added: curr.sortIds.filter(id => !(id in prev.byId)),
    removed: prev.sortIds.filter(id => !(id in curr.byId)),
    parentChanged: curr.sortIds.filter(
      id => id in prev.byId && prev.byId[id].parent !== curr.byId[id].parent
    ),
  })),
  bufferTime(DEFAULT_DEBOUNCE_TIME),
  map((batches): ComponentsDiff => ({
    added: Array.from(new Set(batches.flatMap(b => b.added))),
    removed: Array.from(new Set(batches.flatMap(b => b.removed))),
    parentChanged: Array.from(new Set(batches.flatMap(b => b.parentChanged))),
  })),
  filter(
    ({ added, removed, parentChanged }) =>
      added.length > 0 || removed.length > 0 || parentChanged.length > 0
  ),
  share()
);
